import { Icon, Icons } from '@/components/icon';
import {
  Button,
  Modal,
  PageContent,
  PageHeader,
  PageLayout,
  TextInput,
} from '@/components/ui/design-system';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  type OAuthApp,
  type OAuthAppRegistration,
  approveOAuthApp,
  createOAuthApp,
  getOAuthApps,
  rejectOAuthApp,
  suspendOAuthApp,
  updateOAuthApp,
} from '@/services/api-allo/internal';
import { OAUTH_SCOPE_LABELS } from '@/services/api-allo/oauth-scopes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Check, Copy, Pencil, Plus, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

const AVAILABLE_SCOPES = [
  'CONVERSATIONS_READ',
  'CONVERSATIONS_WRITE',
  'CONTACTS_READ',
  'CONTACTS_READ_WRITE',
  'SMS_SEND',
  'DIALING_QUEUE_READ_WRITE',
  'PHONE_NUMBERS_READ',
  'TAGS_READ',
  'TAGS_WRITE',
  'USERS_READ',
  'CRM_READ',
  'CRM_WRITE',
  'CRM_DELETE',
];

const VISIBILITY_OPTIONS: {
  value: 'PRIVATE' | 'TEAM_SCOPED' | 'PUBLIC';
  label: string;
  description: string;
}[] = [
  { value: 'PRIVATE', label: 'Private', description: 'Only the app creator can use it' },
  {
    value: 'TEAM_SCOPED',
    label: 'Team Scoped',
    description: 'Only members of linked teams can authorize',
  },
  {
    value: 'PUBLIC',
    label: 'Public',
    description: 'Any Allo user can authorize (requires approval)',
  },
];

export const Route = createFileRoute('/_app/internal/oauth')({
  beforeLoad: async () => {
    try {
      await getOAuthApps();
    } catch (error) {
      if (error instanceof Error && error.message === 'FORBIDDEN') {
        throw redirect({ to: '/', replace: true });
      }
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingApp, setEditingApp] = useState<OAuthApp | null>(null);
  const [createdSecret, setCreatedSecret] = useState<{
    clientId: string;
    clientSecret: string;
  } | null>(null);

  const appsQuery = useQuery({
    queryKey: ['internal-oauth-apps'],
    queryFn: () => getOAuthApps(),
    retry: false,
  });

  const rows = appsQuery.data ?? [];

  return (
    <PageLayout>
      <PageHeader title="OAuth Apps" />
      <PageContent>
        <div className="flex h-full flex-col gap-4 p-4">
          <div className="flex h-full flex-col gap-4">
            <div className="flex items-center justify-between">
              <p className="text-body-14 text-primary/50">
                Register and manage OAuth apps for third-party integrations.
              </p>
              <button
                type="button"
                className="inline-flex h-9 items-center gap-2 rounded-md border border-primary/15 bg-white px-3.5 text-body-14 text-primary transition-colors hover:bg-primary/[0.04]"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="size-4" />
                New App
              </button>
            </div>

            {createdSecret && (
              <SecretBanner
                clientId={createdSecret.clientId}
                clientSecret={createdSecret.clientSecret}
                onDismiss={() => setCreatedSecret(null)}
              />
            )}

            <div className="min-h-0 flex-1 overflow-auto rounded-2xl border border-primary/10 bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="border-primary/10">
                    <TableHead className="h-12 px-4 text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Name
                    </TableHead>
                    <TableHead className="px-4 text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Client ID
                    </TableHead>
                    <TableHead className="px-4 text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Visibility
                    </TableHead>
                    <TableHead className="px-4 text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Status
                    </TableHead>
                    <TableHead className="px-4 text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Scopes
                    </TableHead>
                    <TableHead className="px-4 text-right text-body-12 font-semibold uppercase tracking-[0.08em] text-primary/45">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appsQuery.isLoading ? (
                    <EmptyRow
                      message="Loading apps"
                      sub="Fetching registered OAuth applications."
                    />
                  ) : appsQuery.error ? (
                    <EmptyRow message="Could not load apps" sub="Refresh the page and try again." />
                  ) : rows.length === 0 ? (
                    <EmptyRow
                      message="No OAuth apps registered"
                      sub='Click "New App" to register your first OAuth application.'
                    />
                  ) : (
                    rows.map((app) => (
                      <AppRow
                        key={app.pid}
                        app={app}
                        onEdit={() => setEditingApp(app)}
                        onUpdated={() =>
                          queryClient.invalidateQueries({
                            queryKey: ['internal-oauth-apps'],
                          })
                        }
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        <CreateAppModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={(result) => {
            setShowCreateModal(false);
            setCreatedSecret({
              clientId: result.app.client_id,
              clientSecret: result.client_secret,
            });
            queryClient.invalidateQueries({ queryKey: ['internal-oauth-apps'] });
          }}
        />

        {editingApp && (
          <EditAppModal
            app={editingApp}
            open={!!editingApp}
            onClose={() => setEditingApp(null)}
            onUpdated={() => {
              setEditingApp(null);
              queryClient.invalidateQueries({ queryKey: ['internal-oauth-apps'] });
            }}
          />
        )}
      </PageContent>
    </PageLayout>
  );
}

const EmptyRow = ({ message, sub }: { message: string; sub: string }) => (
  <TableRow className="border-primary/10">
    <TableCell colSpan={6} className="px-4 py-12 text-center">
      <div className="space-y-1">
        <div className="text-body-14 text-primary">{message}</div>
        <div className="text-body-14 text-primary/50">{sub}</div>
      </div>
    </TableCell>
  </TableRow>
);

const StatusBadge = ({ status }: { status: string }) => {
  const colors: Record<string, string> = {
    DRAFT: 'border-primary/10 bg-primary/[0.04] text-primary/70',
    PENDING_REVIEW: 'border-amber-200 bg-amber-50 text-amber-700',
    APPROVED: 'border-green-200 bg-green-50 text-green-700',
    REJECTED: 'border-red-200 bg-red-50 text-red-700',
    SUSPENDED: 'border-red-200 bg-red-50 text-red-700',
  };

  return (
    <span
      className={cn(
        'inline-flex rounded-full border px-2.5 py-1 text-body-12',
        colors[status] ?? colors.DRAFT,
      )}
    >
      {status.replace('_', ' ')}
    </span>
  );
};

const AppRow = ({
  app,
  onEdit,
  onUpdated,
}: { app: OAuthApp; onEdit: () => void; onUpdated: () => void }) => {
  const approveMutation = useMutation({
    mutationFn: () => approveOAuthApp(app.pid, 'Approved via internal tool'),
    onSuccess: () => {
      toast.success(`${app.name} approved`);
      onUpdated();
    },
    onError: () => toast.error('Failed to approve app'),
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectOAuthApp(app.pid, 'Rejected via internal tool'),
    onSuccess: () => {
      toast.success(`${app.name} rejected`);
      onUpdated();
    },
    onError: () => toast.error('Failed to reject app'),
  });

  const suspendMutation = useMutation({
    mutationFn: () => suspendOAuthApp(app.pid, 'Suspended via internal tool'),
    onSuccess: () => {
      toast.success(`${app.name} suspended`);
      onUpdated();
    },
    onError: () => toast.error('Failed to suspend app'),
  });

  const isPending =
    approveMutation.isPending || rejectMutation.isPending || suspendMutation.isPending;

  return (
    <TableRow className="border-primary/10 bg-white transition-colors hover:bg-primary/[0.02]">
      <TableCell className="px-4 py-3.5">
        <div className="flex items-center gap-2">
          {app.logo_url && (
            <img src={app.logo_url} alt="" className="size-6 rounded-md object-cover" />
          )}
          <div>
            <div className="font-medium text-primary">{app.name}</div>
            {app.description && (
              <div className="text-body-12 text-primary/50">{app.description}</div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="px-4 py-3.5">
        <code className="text-body-12 text-primary/60">{app.client_id}</code>
      </TableCell>
      <TableCell className="px-4 py-3.5">
        <span className="inline-flex rounded-full border border-primary/10 bg-primary/[0.04] px-2.5 py-1 text-body-12 text-primary/70">
          {app.visibility}
        </span>
      </TableCell>
      <TableCell className="px-4 py-3.5">
        <StatusBadge status={app.status} />
      </TableCell>
      <TableCell className="px-4 py-3.5">
        <div className="flex flex-wrap gap-1">
          {app.scopes.slice(0, 3).map((scope) => (
            <span
              key={scope}
              className="inline-flex rounded-full border border-primary/10 bg-primary/[0.04] px-2 py-0.5 text-body-11 text-primary/60"
            >
              {scope}
            </span>
          ))}
          {app.scopes.length > 3 && (
            <span className="inline-flex rounded-full border border-primary/10 bg-primary/[0.04] px-2 py-0.5 text-body-11 text-primary/60">
              +{app.scopes.length - 3}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="px-4 py-3.5 text-right">
        <div className="inline-flex gap-2">
          <button
            type="button"
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-primary/15 bg-white px-3 text-body-13 text-primary transition-colors hover:bg-primary/[0.04]"
            onClick={onEdit}
          >
            <Pencil className="size-3.5" />
            Edit
          </button>
          {app.status === 'PENDING_REVIEW' && (
            <>
              <button
                type="button"
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-3 text-body-13 text-green-700 transition-colors hover:bg-green-100 disabled:opacity-50"
                disabled={isPending}
                onClick={() => approveMutation.mutate()}
              >
                <Check className="size-3.5" />
                Approve
              </button>
              <button
                type="button"
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 text-body-13 text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
                disabled={isPending}
                onClick={() => rejectMutation.mutate()}
              >
                <X className="size-3.5" />
                Reject
              </button>
            </>
          )}
          {app.status === 'APPROVED' && (
            <button
              type="button"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 text-body-13 text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
              disabled={isPending}
              onClick={() => suspendMutation.mutate()}
            >
              Suspend
            </button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

const ScopeSelector = ({
  selected,
  onToggle,
  disabled,
}: { selected: string[]; onToggle: (scope: string) => void; disabled?: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);

  const label =
    selected.length === 0
      ? 'Select permissions'
      : `${selected.length} permission${selected.length > 1 ? 's' : ''} selected`;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-7 w-full items-center justify-between rounded-md border border-primary-10 bg-white px-3 text-body-13 outline-none transition-colors',
            'hover:border-primary-20',
            'focus:border-highlight-default focus-visible:border-highlight-default',
            isOpen && 'border-highlight-default',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <span className={cn(selected.length === 0 ? 'text-primary-40' : 'text-primary')}>
            {label}
          </span>
          <Icon name={Icons.ChevronDown} className="text-s text-primary-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="z-modal w-[var(--radix-popover-trigger-width)] rounded-xl border border-primary-10 bg-white p-1.5 shadow-md"
        align="start"
      >
        <div className="flex max-h-[300px] flex-col overflow-y-auto">
          {AVAILABLE_SCOPES.map((scope) => {
            const info = OAUTH_SCOPE_LABELS[scope];
            return (
              <button
                key={scope}
                type="button"
                onClick={() => onToggle(scope)}
                disabled={disabled}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 rounded-lg p-2.5 text-left transition-colors hover:bg-primary-3',
                  disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <div className="flex flex-1 flex-col gap-0.5">
                  <span className="text-body-13-bold text-primary">{info?.name ?? scope}</span>
                  <span className="text-body-12 text-primary-50">{info?.description ?? ''}</span>
                </div>
                <div className="flex size-6 shrink-0 items-center justify-center">
                  <div
                    className={cn(
                      'flex size-3.5 items-center justify-center rounded-xs border transition-colors',
                      selected.includes(scope)
                        ? 'border-highlight-default bg-highlight-default'
                        : 'border-primary-20 bg-white',
                    )}
                  >
                    {selected.includes(scope) && (
                      <Icon name={Icons.Check} className="text-xs text-white" />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const CreateAppModal = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (result: OAuthAppRegistration) => void;
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['CONVERSATIONS_READ']);

  const createMutation = useMutation({
    mutationFn: () =>
      createOAuthApp({
        name,
        description: description || undefined,
        redirect_uris: redirectUri
          .split(',')
          .map((u) => u.trim())
          .filter(Boolean),
        scopes: selectedScopes,
        app_type: 'CONFIDENTIAL',
        logo_url: logoUrl || undefined,
      }),
    onSuccess: (result) => {
      onCreated(result);
      setName('');
      setDescription('');
      setRedirectUri('');
      setLogoUrl('');
      setSelectedScopes(['CONVERSATIONS_READ']);
      toast.success('OAuth app created');
    },
    onError: () => toast.error('Failed to create app'),
  });

  const handleClose = () => {
    if (!createMutation.isPending) {
      onClose();
    }
  };

  const toggleScope = useCallback((scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  }, []);

  const actions = [
    <Button
      key="cancel"
      variant="outline"
      size="sm"
      onClick={handleClose}
      disabled={createMutation.isPending}
    >
      Cancel
    </Button>,
    <Button
      key="submit"
      variant="primary"
      size="sm"
      onClick={() => createMutation.mutate()}
      isLoading={createMutation.isPending}
      disabled={!name || !redirectUri || selectedScopes.length === 0}
    >
      Create App
    </Button>,
  ];

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Register New OAuth App"
      actions={actions}
      closeOnBackdrop={!createMutation.isPending}
      size="md"
      withScrollArea={false}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">App Name</span>
          <TextInput
            autoFocus
            variant="highlight"
            className="h-7"
            placeholder="My Integration"
            value={name}
            onChange={(e) => setName(e.target.value)}
            isDisabled={createMutation.isPending}
            fit="parent"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">Description (optional)</span>
          <TextInput
            variant="highlight"
            className="h-7"
            placeholder="What does this app do?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            isDisabled={createMutation.isPending}
            fit="parent"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">Redirect URIs (comma-separated)</span>
          <TextInput
            variant="highlight"
            className="h-7"
            placeholder="https://example.com/callback"
            value={redirectUri}
            onChange={(e) => setRedirectUri(e.target.value)}
            isDisabled={createMutation.isPending}
            fit="parent"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">Logo URL (optional)</span>
          <TextInput
            variant="highlight"
            className="h-7"
            placeholder="https://example.com/logo.png"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            isDisabled={createMutation.isPending}
            fit="parent"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">Scopes</span>
          <ScopeSelector
            selected={selectedScopes}
            onToggle={toggleScope}
            disabled={createMutation.isPending}
          />
        </div>
      </div>
    </Modal>
  );
};

const EditAppModal = ({
  app,
  open,
  onClose,
  onUpdated,
}: {
  app: OAuthApp;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
}) => {
  const [name, setName] = useState(app.name);
  const [description, setDescription] = useState(app.description ?? '');
  const [redirectUri, setRedirectUri] = useState(app.redirect_uris.join(', '));
  const [logoUrl, setLogoUrl] = useState(app.logo_url ?? '');
  const [visibility, setVisibility] = useState(app.visibility);
  const [selectedScopes, setSelectedScopes] = useState<string[]>(app.scopes);

  const updateMutation = useMutation({
    mutationFn: () =>
      updateOAuthApp(app.pid, {
        name,
        description: description || undefined,
        redirect_uris: redirectUri
          .split(',')
          .map((u) => u.trim())
          .filter(Boolean),
        scopes: selectedScopes,
        visibility,
        logo_url: logoUrl || undefined,
      }),
    onSuccess: () => {
      toast.success('App updated');
      onUpdated();
    },
    onError: () => toast.error('Failed to update app'),
  });

  const handleClose = () => {
    if (!updateMutation.isPending) {
      onClose();
    }
  };

  const toggleScope = useCallback((scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  }, []);

  const actions = [
    <Button
      key="cancel"
      variant="outline"
      size="sm"
      onClick={handleClose}
      disabled={updateMutation.isPending}
    >
      Cancel
    </Button>,
    <Button
      key="submit"
      variant="primary"
      size="sm"
      onClick={() => updateMutation.mutate()}
      isLoading={updateMutation.isPending}
      disabled={!name || selectedScopes.length === 0}
    >
      Save Changes
    </Button>,
  ];

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={`Edit: ${app.name}`}
      actions={actions}
      closeOnBackdrop={!updateMutation.isPending}
      size="md"
      withScrollArea={false}
    >
      <div className="flex flex-col gap-3">
        <div className="rounded-xl border border-primary-10 bg-primary-5/50 px-3 py-2">
          <span className="text-body-12 text-primary-40">Client ID</span>
          <code className="block text-body-13 text-primary-80">{app.client_id}</code>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">App Name</span>
          <TextInput
            variant="highlight"
            className="h-7"
            value={name}
            onChange={(e) => setName(e.target.value)}
            isDisabled={updateMutation.isPending}
            fit="parent"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">Description</span>
          <TextInput
            variant="highlight"
            className="h-7"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            isDisabled={updateMutation.isPending}
            fit="parent"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">Redirect URIs (comma-separated)</span>
          <TextInput
            variant="highlight"
            className="h-7"
            value={redirectUri}
            onChange={(e) => setRedirectUri(e.target.value)}
            isDisabled={updateMutation.isPending}
            fit="parent"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">Logo URL</span>
          <TextInput
            variant="highlight"
            className="h-7"
            placeholder="https://example.com/logo.png"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            isDisabled={updateMutation.isPending}
            fit="parent"
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">Visibility</span>
          <div className="flex flex-col gap-2">
            {VISIBILITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setVisibility(opt.value)}
                className={cn(
                  'flex items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
                  visibility === opt.value
                    ? 'border-green-300 bg-green-50'
                    : 'border-primary-10 bg-white hover:bg-primary-5/50',
                )}
              >
                <div
                  className={cn(
                    'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
                    visibility === opt.value
                      ? 'border-green-500 bg-green-500'
                      : 'border-primary-20',
                  )}
                >
                  {visibility === opt.value && <div className="size-1.5 rounded-full bg-white" />}
                </div>
                <div>
                  <div
                    className={cn(
                      'text-body-13 font-medium',
                      visibility === opt.value ? 'text-green-800' : 'text-primary-80',
                    )}
                  >
                    {opt.label}
                  </div>
                  <div
                    className={cn(
                      'text-body-12',
                      visibility === opt.value ? 'text-green-600' : 'text-primary-40',
                    )}
                  >
                    {opt.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-body-12 text-primary-60">Scopes</span>
          <ScopeSelector
            selected={selectedScopes}
            onToggle={toggleScope}
            disabled={updateMutation.isPending}
          />
        </div>
      </div>
    </Modal>
  );
};

const SecretBanner = ({
  clientId,
  clientSecret,
  onDismiss,
}: {
  clientId: string;
  clientSecret: string;
  onDismiss: () => void;
}) => {
  const [copied, setCopied] = useState<'id' | 'secret' | null>(null);

  const copyToClipboard = useCallback((text: string, type: 'id' | 'secret') => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-body-14 font-semibold text-amber-800">Save your client secret now</h3>
          <p className="text-body-13 mt-1 text-amber-700">
            This is the only time the client secret will be shown.
          </p>
        </div>
        <button type="button" onClick={onDismiss} className="text-amber-600 hover:text-amber-800">
          <X className="size-4" />
        </button>
      </div>
      <div className="mt-3 flex flex-col gap-2">
        <CopyField
          label="Client ID"
          value={clientId}
          copied={copied === 'id'}
          onCopy={() => copyToClipboard(clientId, 'id')}
        />
        <CopyField
          label="Secret"
          value={clientSecret}
          copied={copied === 'secret'}
          onCopy={() => copyToClipboard(clientSecret, 'secret')}
        />
      </div>
    </div>
  );
};

const CopyField = ({
  label,
  value,
  copied,
  onCopy,
}: { label: string; value: string; copied: boolean; onCopy: () => void }) => (
  <div className="flex items-center gap-2">
    <span className="w-24 text-body-12 font-medium text-amber-700">{label}:</span>
    <code className="flex-1 truncate text-body-12 text-amber-900">{value}</code>
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex size-7 items-center justify-center rounded-md hover:bg-amber-100"
    >
      {copied ? (
        <Check className="size-3.5 text-green-600" />
      ) : (
        <Copy className="size-3.5 text-amber-600" />
      )}
    </button>
  </div>
);
